import '@googleworkspace/drive-picker-element';

import React, { useEffect } from 'react';

export interface PickerDocument {
  id: string;
  serviceId: string;
  mimeType: string;
  name: string;
  description: string;
  type: string;
  lastEditedUtc: number;
  iconUrl: string;
  url: string;
  embedUrl: string;
  sizeBytes: number;
  parentId: string;
  organizationDisplayName: string;
}

export interface PickerEventDetail {
  action: 'picked' | 'authenticated' | 'canceled';
  viewToken: [string, any, { mimeTypes: string; selectFolder: boolean; includeFolders: boolean }];
  docs?: PickerDocument[];
}

export interface PickerCustomEvent extends CustomEvent {
  detail: PickerEventDetail;
}

type GoogleDrivePickerProps = {
  accessToken: string;
  onSelect: (event: PickerCustomEvent) => void;
  onAuthenticated?: (event: PickerCustomEvent) => void;
  onCancel?: (event: PickerCustomEvent) => void;
  pickerProps?: Record<string, any>;
  docsViewProps?: Record<string, any>;
};

export const GoogleDrivePicker = ({
  accessToken,
  onSelect,
  onAuthenticated,
  onCancel,
  pickerProps = {},
  docsViewProps = {},
}: GoogleDrivePickerProps) => {
  const ref = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const element = ref.current;

    const handlePickerEvent =
      (eventType: string): ((e: Event) => void) =>
      (e: Event) => {
        const customEvent = e as PickerCustomEvent;
        switch (eventType) {
          case 'picked':
            onSelect(customEvent);
            break;
          case 'authenticated':
            onAuthenticated?.(customEvent);
            break;
          case 'canceled':
            onCancel?.(customEvent);
            break;
          default:
            break;
        }
      };

    const eventListeners: Array<[string, (e: Event) => void]> = [
      ['picker:picked', handlePickerEvent('picked')],
    ];

    if (onAuthenticated) {
      eventListeners.push(['picker:authenticated', handlePickerEvent('authenticated')]);
    }
    if (onCancel) {
      eventListeners.push(['picker:canceled', handlePickerEvent('canceled')]);
    }

    eventListeners.forEach(([event, handler]) => element.addEventListener(event, handler));

    return () => {
      eventListeners.forEach(([event, handler]) => element.removeEventListener(event, handler));
    };
  }, [onSelect, onAuthenticated, onCancel]);

  return (
    <drive-picker ref={ref} oauth-token={accessToken} {...pickerProps}>
      <drive-picker-docs-view
        include-folders
        select-folder-enabled
        mime-types="application/vnd.google-apps.folder"
        {...docsViewProps}
      />
    </drive-picker>
  );
};
